import React, { useState } from 'react';
import { Gift, Plus, DollarSign, Edit2, Trash2, X, Lock } from 'lucide-react';
import { formatNumber } from '../../utils';

export const AirdropsReceivedTab = ({ receivedAirdrops = [], setReceivedAirdrops, projectSettings = {}, isDark, colors, isPro, openPricing }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAirdrop, setNewAirdrop] = useState({ project: '', tokens: '', totalSold: '' });
  const [saleForm, setSaleForm] = useState({ airdropId: null, percentage: '', pricePerToken: '' });
  const [editingAirdrop, setEditingAirdrop] = useState(null);
  const [editAirdropForm, setEditAirdropForm] = useState({ project: '', tokens: '' });
  const [editingSale, setEditingSale] = useState({ airdropId: null, saleId: null });
  const [editSaleForm, setEditSaleForm] = useState({ percentage: '', pricePerToken: '' });

  const allProjects = Object.keys(projectSettings);
  const FREE_RECEIVED_LIMIT = 5;

  const addAirdrop = () => {
    if (!newAirdrop.project || !newAirdrop.tokens) {
      alert('Please enter the project and tokens received');
      return;
    }

    // Check Free tier limit
    if (!isPro && receivedAirdrops.length >= FREE_RECEIVED_LIMIT) {
      alert('Free plan is limited to 5 received airdrops. Upgrade to Pro for unlimited!');
      if (openPricing) openPricing();
      return;
    }

    const airdrop = {
      id: Date.now(),
      project: newAirdrop.project,
      tokens: parseFloat(newAirdrop.tokens),
      totalSold: parseFloat(newAirdrop.totalSold) || 0,
      sales: newAirdrop.totalSold ? [{
        id: Date.now(),
        percentage: 100,
        pricePerToken: parseFloat(newAirdrop.totalSold) / parseFloat(newAirdrop.tokens),
        total: parseFloat(newAirdrop.totalSold),
        date: new Date().toISOString()
      }] : [],
      dateReceived: new Date().toISOString()
    };

    setReceivedAirdrops([...receivedAirdrops, airdrop]);
    setNewAirdrop({ project: '', tokens: '', totalSold: '' });
    setShowAddForm(false);
  };

  const deleteAirdrop = (airdropId) => {
    if (confirm('Are you sure you want to delete this airdrop?')) {
      setReceivedAirdrops(receivedAirdrops.filter(a => a.id !== airdropId));
    }
  };

  const startEditAirdrop = (airdrop) => {
    setEditingAirdrop(airdrop.id);
    setEditAirdropForm({ project: airdrop.project, tokens: airdrop.tokens.toString() });
  };

  const saveEditAirdrop = (airdropId) => {
    const updatedAirdrops = receivedAirdrops.map(a => {
      if (a.id === airdropId) {
        return { ...a, project: editAirdropForm.project, tokens: parseFloat(editAirdropForm.tokens) };
      }
      return a;
    });
    setReceivedAirdrops(updatedAirdrops);
    setEditingAirdrop(null);
    setEditAirdropForm({ project: '', tokens: '' });
  };

  const cancelEditAirdrop = () => {
    setEditingAirdrop(null);
    setEditAirdropForm({ project: '', tokens: '' });
  };

  const addSale = (airdropId) => {
    if (!saleForm.percentage || !saleForm.pricePerToken) {
      alert('Please enter percentage and price per token');
      return;
    }

    const airdrop = receivedAirdrops.find(a => a.id === airdropId);
    if (!airdrop) return;

    const tokensToSell = (airdrop.tokens * parseFloat(saleForm.percentage)) / 100;
    const saleTotal = tokensToSell * parseFloat(saleForm.pricePerToken);

    const sale = {
      id: Date.now(),
      percentage: parseFloat(saleForm.percentage),
      pricePerToken: parseFloat(saleForm.pricePerToken),
      tokens: tokensToSell,
      total: saleTotal,
      date: new Date().toISOString()
    };

    const updatedAirdrops = receivedAirdrops.map(a => {
      if (a.id === airdropId) {
        return { ...a, sales: [...(a.sales || []), sale], totalSold: (a.totalSold || 0) + saleTotal };
      }
      return a;
    });

    setReceivedAirdrops(updatedAirdrops);
    setSaleForm({ airdropId: null, percentage: '', pricePerToken: '' });
  };

  const deleteSale = (airdropId, saleId) => {
    if (confirm('Are you sure you want to delete this sale?')) {
      const updatedAirdrops = receivedAirdrops.map(a => {
        if (a.id === airdropId) {
          const saleToDelete = a.sales.find(s => s.id === saleId);
          const newSales = a.sales.filter(s => s.id !== saleId);
          const newTotalSold = (a.totalSold || 0) - (saleToDelete?.total || 0);
          return { ...a, sales: newSales, totalSold: Math.max(0, newTotalSold) };
        }
        return a;
      });
      setReceivedAirdrops(updatedAirdrops);
    }
  };

  const startEditSale = (airdropId, sale) => {
    setEditingSale({ airdropId, saleId: sale.id });
    setEditSaleForm({ percentage: sale.percentage.toString(), pricePerToken: sale.pricePerToken.toString() });
  };

  const saveEditSale = (airdropId, saleId) => {
    const airdrop = receivedAirdrops.find(a => a.id === airdropId);
    if (!airdrop) return;

    const oldSale = airdrop.sales.find(s => s.id === saleId);
    const tokensToSell = (airdrop.tokens * parseFloat(editSaleForm.percentage)) / 100;
    const newSaleTotal = tokensToSell * parseFloat(editSaleForm.pricePerToken);

    const updatedAirdrops = receivedAirdrops.map(a => {
      if (a.id === airdropId) {
        const updatedSales = a.sales.map(s => {
          if (s.id === saleId) {
            return {
              ...s,
              percentage: parseFloat(editSaleForm.percentage),
              pricePerToken: parseFloat(editSaleForm.pricePerToken),
              tokens: tokensToSell,
              total: newSaleTotal
            };
          }
          return s;
        });
        const newTotalSold = (a.totalSold || 0) - (oldSale?.total || 0) + newSaleTotal;
        return { ...a, sales: updatedSales, totalSold: newTotalSold };
      }
      return a;
    });

    setReceivedAirdrops(updatedAirdrops);
    setEditingSale({ airdropId: null, saleId: null });
    setEditSaleForm({ percentage: '', pricePerToken: '' });
  };

  const cancelEditSale = () => {
    setEditingSale({ airdropId: null, saleId: null });
    setEditSaleForm({ percentage: '', pricePerToken: '' });
  };

  const getTotalSoldPercentage = (airdrop) => {
    if (!airdrop.sales || airdrop.sales.length === 0) return 0;
    return airdrop.sales.reduce((sum, sale) => sum + sale.percentage, 0);
  };

  const inputClasses = `w-full border rounded px-3 py-2 focus:border-blue-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-2xl font-bold flex items-center gap-2 ${colors.textPrimary}`}>
              <Gift size={28} className="text-pink-400" />
              Airdrops Received
            </h3>
            {!isPro && (
              <p className={`text-sm mt-1 ${colors.textSecondary}`}>
                <Lock size={12} className="inline mr-1" />
                {receivedAirdrops.length}/{FREE_RECEIVED_LIMIT} airdrops (Free plan)
              </p>
            )}
          </div>
          <button onClick={() => setShowAddForm(!showAddForm)} className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            <Plus size={20} />
            Add Airdrop
          </button>
        </div>

        {showAddForm && (
          <div className={`rounded-lg p-5 border mb-6 ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
            <h4 className={`text-lg font-bold mb-4 ${colors.textPrimary}`}>New Airdrop Received</h4>
            
            {!isPro && receivedAirdrops.length >= FREE_RECEIVED_LIMIT && (
              <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-yellow-900/20 border-yellow-700/30 text-yellow-400' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                <p className="font-semibold">Free plan limit reached</p>
                <p className="text-sm mt-1">Upgrade to Pro for unlimited received airdrops.</p>
                <button onClick={openPricing} className="mt-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm font-semibold">
                  Upgrade to Pro
                </button>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Project</label>
                <select value={newAirdrop.project} onChange={(e) => setNewAirdrop({ ...newAirdrop, project: e.target.value })} className={inputClasses}>
                  <option value="">Select...</option>
                  {allProjects.map(project => <option key={project} value={project}>{project}</option>)}
                </select>
              </div>
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>Tokens Received</label>
                <input type="number" value={newAirdrop.tokens} onChange={(e) => setNewAirdrop({ ...newAirdrop, tokens: e.target.value })} placeholder="1000" className={inputClasses} />
              </div>
              <div>
                <label className={`block text-sm mb-2 ${colors.textSecondary}`}>$ Total Sold (Optional)</label>
                <input type="number" value={newAirdrop.totalSold} onChange={(e) => setNewAirdrop({ ...newAirdrop, totalSold: e.target.value })} placeholder="5000" className={inputClasses} />
              </div>
            </div>

            <p className={`text-xs mb-4 ${colors.textSecondary}`}>💡 If you fill "$ Total Sold", a 100% sale will be registered</p>

            <div className="flex gap-2">
              <button onClick={addAirdrop} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">Save</button>
              <button onClick={() => setShowAddForm(false)} className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
            </div>
          </div>
        )}

        {receivedAirdrops.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <Gift size={48} className="mx-auto mb-4 opacity-50" />
            <p>No airdrops received yet</p>
            <p className="text-sm mt-2">Register airdrops here to track your sales</p>
          </div>
        ) : (
          <div className="space-y-4">
            {receivedAirdrops.map(airdrop => {
              const soldPercentage = getTotalSoldPercentage(airdrop);
              const remainingTokens = airdrop.tokens * (1 - soldPercentage / 100);
              const showSaleForm = saleForm.airdropId === airdrop.id;
              const isEditingThisAirdrop = editingAirdrop === airdrop.id;

              return (
                <div key={airdrop.id} className={`rounded-lg p-5 border ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  {isEditingThisAirdrop ? (
                    <div className="mb-4">
                      <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>Edit Airdrop</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className={`block text-xs mb-1 ${colors.textSecondary}`}>Project</label>
                          <select value={editAirdropForm.project} onChange={(e) => setEditAirdropForm({ ...editAirdropForm, project: e.target.value })} className={inputClasses}>
                            <option value="">Select...</option>
                            {allProjects.map(project => <option key={project} value={project}>{project}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={`block text-xs mb-1 ${colors.textSecondary}`}>Tokens Received</label>
                          <input type="number" value={editAirdropForm.tokens} onChange={(e) => setEditAirdropForm({ ...editAirdropForm, tokens: e.target.value })} className={inputClasses} />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => saveEditAirdrop(airdrop.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors">Save</button>
                        <button onClick={cancelEditAirdrop} className={`px-3 py-1 rounded text-sm transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className={`text-xl font-bold ${colors.textPrimary}`}>{airdrop.project}</h4>
                        <div className={`text-sm mt-1 ${colors.textSecondary}`}>
                          {formatNumber(airdrop.tokens)} tokens • {formatNumber(remainingTokens)} remaining
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right mr-4">
                          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>${formatNumber(airdrop.totalSold || 0)}</div>
                          <div className={`text-xs ${colors.textSecondary}`}>Total sold</div>
                        </div>
                        <button onClick={() => startEditAirdrop(airdrop)} className="text-blue-400 hover:text-blue-300 p-2"><Edit2 size={18} /></button>
                        <button onClick={() => deleteAirdrop(airdrop.id)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  )}

                  <div className="mb-4">
                    <div className={`flex justify-between text-xs mb-1 ${colors.textSecondary}`}>
                      <span>Sale progress</span>
                      <span>{soldPercentage.toFixed(1)}%</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-600' : 'bg-gray-300'}`}>
                      <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all duration-500" style={{ width: `${soldPercentage}%` }} />
                    </div>
                  </div>

                  {airdrop.sales && airdrop.sales.length > 0 && (
                    <div className="mb-4">
                      <div className={`text-sm mb-2 ${colors.textSecondary}`}>Sales: {airdrop.sales.length}</div>
                      <div className="space-y-2">
                        {airdrop.sales.map(sale => {
                          const isEditingThisSale = editingSale.airdropId === airdrop.id && editingSale.saleId === sale.id;
                          
                          return isEditingThisSale ? (
                            <div key={sale.id} className={`rounded px-3 py-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                              <div className="grid grid-cols-2 gap-3 mb-2">
                                <div>
                                  <label className={`block text-xs mb-1 ${colors.textSecondary}`}>% Sold</label>
                                  <input type="number" value={editSaleForm.percentage} onChange={(e) => setEditSaleForm({ ...editSaleForm, percentage: e.target.value })} className={`${inputClasses} text-sm`} />
                                </div>
                                <div>
                                  <label className={`block text-xs mb-1 ${colors.textSecondary}`}>Price/Token ($)</label>
                                  <input type="number" value={editSaleForm.pricePerToken} onChange={(e) => setEditSaleForm({ ...editSaleForm, pricePerToken: e.target.value })} step="0.01" className={`${inputClasses} text-sm`} />
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button onClick={() => saveEditSale(airdrop.id, sale.id)} className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs transition-colors">Save</button>
                                <button onClick={cancelEditSale} className={`px-2 py-1 rounded text-xs transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div key={sale.id} className={`flex items-center justify-between text-xs rounded px-3 py-2 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                              <span className={colors.textSecondary}>{sale.percentage}% @ ${sale.pricePerToken?.toFixed(4)}/token</span>
                              <div className="flex items-center gap-2">
                                <span className={isDark ? 'text-green-400' : 'text-green-600'}>${formatNumber(sale.total)}</span>
                                <button onClick={() => startEditSale(airdrop.id, sale)} className="text-blue-400 hover:text-blue-300 p-1"><Edit2 size={14} /></button>
                                <button onClick={() => deleteSale(airdrop.id, sale.id)} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14} /></button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {soldPercentage < 100 && (
                    showSaleForm ? (
                      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                        <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>Register Sale</h5>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className={`block text-xs mb-1 ${colors.textSecondary}`}>% to Sell</label>
                            <input type="number" value={saleForm.percentage} onChange={(e) => setSaleForm({ ...saleForm, percentage: e.target.value })} placeholder="25" max={100 - soldPercentage} className={`${inputClasses} text-sm`} />
                          </div>
                          <div>
                            <label className={`block text-xs mb-1 ${colors.textSecondary}`}>Price/Token ($)</label>
                            <input type="number" value={saleForm.pricePerToken} onChange={(e) => setSaleForm({ ...saleForm, pricePerToken: e.target.value })} placeholder="5.00" step="0.01" className={`${inputClasses} text-sm`} />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => addSale(airdrop.id)} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm transition-colors">Save</button>
                          <button onClick={() => setSaleForm({ airdropId: null, percentage: '', pricePerToken: '' })} className={`px-3 py-1 rounded text-sm transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setSaleForm({ airdropId: airdrop.id, percentage: '', pricePerToken: '' })} className={`w-full py-2 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>
                        <DollarSign size={16} />
                        Register Sale
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
